import { useLayoutEffect, useRef } from "react";
import type { OccurrenceChatMessage } from "@edu/shared";
import { LoadFailed } from "../../lib/loading.tsx";
import { ChatMessage } from "./ChatMessage.tsx";
import { CHAT_FAILED_TEXT } from "./chat-text.ts";
import type { OccurrenceChat } from "./use-occurrence-chat.ts";

/**
 * Лента сообщений со своей прокруткой: общая на все три места показа.
 *
 * ⚠️ **Общая она ради ОДНОГО, и это не разметка, а поведение прокрутки.** Два
 * правила ниже (держаться свежего края и удерживать место под пальцем при
 * догрузке прошлого) ловятся только глазами, тестов на компоненты в проекте нет
 * и не планируется, а второе их написание разъехалось бы с первым молча.
 * Раскладка при этом остаётся за местом показа: класс коробки приезжает
 * пропом, потому что в комнате лента живёт в панели с тремя строками грида, а в
 * карточке занятия в блоке фиксированной высоты.
 *
 * ⚠️ **Своя прокрутка, а не прокрутка контейнера**: форма отправки обязана
 * стоять на месте, пока человек листает вверх за прошлым. Отсюда же
 * `overscroll-behavior: contain` в стилях: докрутив ленту до верха, человек не
 * должен утянуть за ней комнату, карточку или список уроков. В строке списка
 * уроков это обязательно вдвойне: сам список прокручивается по обеим осям, и
 * колесо над лентой цеплялось бы за него.
 */
export function ChatFeed(props: {
  chat: OccurrenceChat;
  /** Класс коробки: раскладка это дело места показа, а не ленты. */
  className: string;
  /** Слова пустой ленты, либо `null`, если лента не пуста. */
  emptyText: string | null;
  /** Одно «сейчас» на всю отрисовку: иначе на границе полуночи ярлыки разойдутся. */
  now: number;
  /**
   * Можно ли убрать это сообщение, либо `null`, если убирать нельзя ничего.
   *
   * ⚠️ В комнате здесь `null`: удаление живёт там, где переписку читают задним
   * числом (разбор у самого `ChatMessage`).
   */
  removable: ((msg: OccurrenceChatMessage) => boolean) | null;
}) {
  const { chat } = props;
  const box = useRef<HTMLDivElement | null>(null);
  /**
   * Человек стоит у свежего края.
   *
   * Начальное «да»: лента открывается на свежем конце. Дальше значение ведёт
   * прокрутка, и от него зависит единственное: догонять ли ленту вниз при новом
   * сообщении. Догонять её у того, кто отлистал вверх и читает прошлое, значит
   * выдёргивать текст из-под глаз.
   */
  const stick = useRef(true);
  /**
   * Что сделать с прокруткой сразу после отрисовки: удержать место под пальцем
   * после догрузки прошлого. Ref, а не состояние: это приказ на один кадр, и
   * вызывать им перерисовку нечего.
   */
  const hold = useRef<{ height: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const node = box.current;
    if (node === null) return;
    const job = hold.current;
    hold.current = null;
    if (job !== null) {
      node.scrollTop = node.scrollHeight - job.height + job.top;
      return;
    }
    if (stick.current) node.scrollTop = node.scrollHeight;
  }, [chat.items]);

  /**
   * Догрузить прошлое, удержав место под пальцем.
   *
   * Высота и положение запоминаются ДО того, как сверху появятся новые строки:
   * иначе лента прыгала бы у того, кто как раз её и листает.
   */
  const openOlder = () => {
    const node = box.current;
    if (node !== null) hold.current = { height: node.scrollHeight, top: node.scrollTop };
    chat.loadOlder();
  };

  return (
    <div
      className={props.className}
      ref={box}
      onScroll={() => {
        const node = box.current;
        if (node === null) return;
        stick.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
      }}
    >
      {/*
        Догрузка прошлого только кнопкой, без ловли прокрутки вверх. Лента узкая
        и короткая, событие прокрутки в ней случается не всегда, а молча
        показывать последнюю дюжину и не говорить, что было раньше, запрещает
        раздел 5.5 CLAUDE.md.
      */}
      {chat.hasOlder &&
        (chat.olderFailed ? (
          /* Отказ ОПРОСА проглатывается молча, а отказ догрузки нет: её человек
             попросил нажатием, и кнопка, не ответившая ничем, читается как
             поломка. Повтор рядом, приём общий на все экраны */
          <LoadFailed text={CHAT_FAILED_TEXT} onRetry={openOlder} />
        ) : (
          <div className="chat__earlier">
            <button
              type="button"
              className="btn btn--quiet"
              disabled={chat.olderBusy}
              onClick={openOlder}
            >
              {chat.olderBusy ? "Загружаем…" : "Более ранние сообщения"}
            </button>
          </div>
        ))}
      {props.emptyText !== null && <p className="muted">{props.emptyText}</p>}
      <ul className="chat__list">
        {chat.items.map((m) => (
          <ChatMessage
            key={m.id}
            msg={m}
            now={props.now}
            onRemove={props.removable?.(m) === true ? chat.remove : null}
          />
        ))}
      </ul>
    </div>
  );
}
